import React, { useState } from 'react';
import { Clock, CheckCircle, Loader2 } from 'lucide-react';
import { colors, spacing, typography, borders, shadows, transitions } from '../../../../tokens';
import { formatTokenAmount, canWithdraw, getRemainingTime } from '../utils/formatters';
import type { PositionInfo } from '../constants';
import { Card } from '../../../../atoms';

interface PositionsListProps {
  positions: PositionInfo[];
  totalPendingRewards: bigint | undefined;
  stakingStep: string;
  onExtendLock: (positionId: number, days: number) => void;
  onWithdraw: (positionId: number, amount: bigint) => void;
  isExtendLockPending: boolean;
  isExtendLockLoading: boolean;
  isWithdrawPending: boolean;
  isWithdrawLoading: boolean;
}

export const PositionsList: React.FC<PositionsListProps> = ({
  positions,
  totalPendingRewards,
  stakingStep,
  onWithdraw,
  isWithdrawPending,
  isWithdrawLoading,
}) => {
  // Hover state for withdraw button
  const [hoveredWithdrawIndex, setHoveredWithdrawIndex] = useState<number | null>(null);

  // Calculate total user weight (sum of all position weights)
  const totalUserWeight = positions.reduce((sum, position) => {
    const weight = (position.amount * position.lockMultiplier) / BigInt(1e18);
    return sum + weight;
  }, 0n);

  // Calculate pending rewards for a specific position based on its weight share
  const calculatePositionRewards = (position: PositionInfo): bigint => {
    if (!totalPendingRewards || totalUserWeight === 0n) return 0n;

    // Position weight = amount × lockMultiplier / 1e18
    const positionWeight = (position.amount * position.lockMultiplier) / BigInt(1e18);

    // Position rewards = (position weight / total user weight) × total pending rewards
    return (positionWeight * totalPendingRewards) / totalUserWeight;
  };

  return (
    <Card>
      <h2 style={{
        fontSize: typography.fontSize['2xl'],
        fontWeight: typography.fontWeight.bold,
        color: colors.text.primary.default,
        margin: `0 0 ${spacing.lg} 0`,
      }}>
        Your Active Positions
      </h2>

      <style>{`
        @media (max-width: 768px) {
          .position-desktop-grid {
            display: none !important;
          }
        }
        @media (min-width: 769px) {
          .position-mobile-card {
            display: none !important;
          }
        }
        @media (min-width: 769px) and (max-width: 1200px) {
          .position-desktop-grid {
            grid-template-columns: 1fr 1fr auto !important;
          }
          .position-hide-tablet {
            display: none !important;
          }
        }
      `}</style>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.md,
      }}>
        {positions.map((position, index) => {
          const multiplier = Number(position.lockMultiplier) / 1e18;
          const unlocked = canWithdraw(position);

          return (
            <React.Fragment key={index}>
              {/* Desktop/Tablet Grid Layout */}
              <div
                className="position-desktop-grid"
                style={{
                  padding: spacing.lg,
                  backgroundColor: colors.background.secondary.default,
                  borderRadius: borders.component.default.radius,
                  border: `1px solid ${colors.border.default.default}`,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr auto',
                  gap: spacing.lg,
                  alignItems: 'center',
                }}
              >
              {/* Amount */}
              <div>
                <div style={{
                  fontSize: typography.fontSize.xs,
                  color: colors.text.muted.default,
                  marginBottom: spacing.xs,
                }}>
                  Staked Amount
                </div>
                <div style={{
                  fontSize: typography.fontSize.lg,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.text.primary.default,
                }}>
                  {formatTokenAmount(position.amount)} FAR
                </div>
              </div>

              {/* Unlock Time */}
              <div>
                <div style={{
                  fontSize: typography.fontSize.xs,
                  color: colors.text.muted.default,
                  marginBottom: spacing.xs,
                }}>
                  Unlock Time
                </div>
                <div style={{
                  fontSize: typography.fontSize.base,
                  fontWeight: typography.fontWeight.semibold,
                  color: colors.text.primary.default,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                }}>
                  <Clock size={16} color={colors.brand.purple.base} />
                  {getRemainingTime(position.unlockTime)}
                </div>
              </div>

              {/* Multiplier */}
              <div className="position-hide-tablet">
                <div style={{
                  fontSize: typography.fontSize.xs,
                  color: colors.text.muted.default,
                  marginBottom: spacing.xs,
                }}>
                  Multiplier
                </div>
                <div style={{
                  fontSize: typography.fontSize.base,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.brand.orange.base,
                }}>
                  {multiplier.toFixed(2)}x
                </div>
              </div>

              {/* Status */}
              <div className="position-hide-tablet">
                <div style={{
                  fontSize: typography.fontSize.xs,
                  color: colors.text.muted.default,
                  marginBottom: spacing.xs,
                }}>
                  Status
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.semibold,
                  color: unlocked ? colors.semantic.success : colors.brand.cyan.base,
                }}>
                  {unlocked ? (
                    <>
                      <CheckCircle size={16} />
                      Unlocked
                    </>
                  ) : (
                    <>
                      <Clock size={16} />
                      Locked
                    </>
                  )}
                </div>
              </div>

              {/* Pending Rewards */}
              <div className="position-hide-tablet">
                <div style={{
                  fontSize: typography.fontSize.xs,
                  color: colors.text.muted.default,
                  marginBottom: spacing.xs,
                }}>
                  Pending Rewards
                </div>
                <div style={{
                  fontSize: typography.fontSize.base,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.semantic.success,
                }}>
                  {formatTokenAmount(calculatePositionRewards(position))} FAR
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.xs,
              }}>
                {/* Withdraw Button */}
                {unlocked && (
                  <button
                    onClick={() => onWithdraw(index, position.amount)}
                    disabled={stakingStep === 'withdrawing' || isWithdrawPending || isWithdrawLoading}
                    onMouseEnter={() => setHoveredWithdrawIndex(index)}
                    onMouseLeave={() => setHoveredWithdrawIndex(null)}
                    style={{
                      padding: `${spacing.xs} ${spacing.sm}`,
                      backgroundColor: colors.semantic.error,
                      color: colors.neutral.white,
                      fontSize: typography.fontSize.sm,
                      fontWeight: typography.fontWeight.semibold,
                      border: 'none',
                      borderRadius: borders.component.button.radius,
                      cursor: (stakingStep === 'withdrawing' || isWithdrawPending || isWithdrawLoading) ? 'not-allowed' : 'pointer',
                      opacity: (stakingStep === 'withdrawing' || isWithdrawPending || isWithdrawLoading) ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: spacing.xs,
                      whiteSpace: 'nowrap',
                      boxShadow: (stakingStep === 'idle' && hoveredWithdrawIndex === index && !isWithdrawPending && !isWithdrawLoading)
                        ? `${shadows.glow.red.lg}, ${shadows.elevation.medium}`
                        : shadows.elevation.low,
                      transform: (stakingStep === 'idle' && hoveredWithdrawIndex === index && !isWithdrawPending && !isWithdrawLoading)
                        ? 'translateY(-2px) scale(1.02)'
                        : 'translateY(0) scale(1)',
                      transition: transitions.button,
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    {(stakingStep === 'withdrawing' || isWithdrawPending || isWithdrawLoading) && (
                      <Loader2 size={14} className="spin" />
                    )}
                    Withdraw
                  </button>
                )}
              </div>
            </div>

            {/* Mobile Card Layout */}
            <div
              className="position-mobile-card"
              style={{
                padding: spacing.lg,
                backgroundColor: colors.background.secondary.default,
                borderRadius: borders.component.default.radius,
                border: `1px solid ${colors.border.default.default}`,
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.md,
              }}
            >
              {/* Amount */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: typography.fontSize.xs, color: colors.text.muted.default }}>
                  Staked Amount
                </span>
                <span style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: colors.text.primary.default }}>
                  {formatTokenAmount(position.amount)} FAR
                </span>
              </div>

              {/* Unlock Time */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: typography.fontSize.xs, color: colors.text.muted.default }}>
                  Unlock Time
                </span>
                <span style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: colors.text.primary.default, display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                  <Clock size={16} color={colors.brand.purple.base} />
                  {getRemainingTime(position.unlockTime)}
                </span>
              </div>

              {/* Multiplier */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: typography.fontSize.xs, color: colors.text.muted.default }}>
                  Multiplier
                </span>
                <span style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.bold, color: colors.brand.orange.base }}>
                  {multiplier.toFixed(2)}x
                </span>
              </div>

              {/* Status */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: typography.fontSize.xs, color: colors.text.muted.default }}>
                  Status
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: unlocked ? colors.semantic.success : colors.brand.cyan.base }}>
                  {unlocked ? (
                    <>
                      <CheckCircle size={16} />
                      Unlocked
                    </>
                  ) : (
                    <>
                      <Clock size={16} />
                      Locked
                    </>
                  )}
                </span>
              </div>

              {/* Pending Rewards */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: typography.fontSize.xs, color: colors.text.muted.default }}>
                  Pending Rewards
                </span>
                <span style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.bold, color: colors.semantic.success }}>
                  {formatTokenAmount(calculatePositionRewards(position))} FAR
                </span>
              </div>

              {/* Withdraw Button */}
              {unlocked && (
                <button
                  onClick={() => onWithdraw(index, position.amount)}
                  disabled={stakingStep === 'withdrawing' || isWithdrawPending || isWithdrawLoading}
                  onMouseEnter={() => setHoveredWithdrawIndex(index)}
                  onMouseLeave={() => setHoveredWithdrawIndex(null)}
                  style={{
                    width: '100%',
                    padding: spacing.md,
                    backgroundColor: colors.semantic.error,
                    color: colors.neutral.white,
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.semibold,
                    border: 'none',
                    borderRadius: borders.component.button.radius,
                    cursor: (stakingStep === 'withdrawing' || isWithdrawPending || isWithdrawLoading) ? 'not-allowed' : 'pointer',
                    opacity: (stakingStep === 'withdrawing' || isWithdrawPending || isWithdrawLoading) ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: spacing.xs,
                    boxShadow: shadows.elevation.low,
                    transition: transitions.button,
                  }}
                >
                  {(stakingStep === 'withdrawing' || isWithdrawPending || isWithdrawLoading) && (
                    <Loader2 size={14} className="spin" />
                  )}
                  Withdraw
                </button>
              )}
            </div>
          </React.Fragment>
          );
        })}
      </div>
    </Card>
  );
};
